import { useEffect, useState } from 'react';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { env } from '@/lib/env';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useNotificationStore } from '@/stores/notifications';
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  notificationKeys,
} from './hooks';
import type { Notification } from './api';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function buildNotificationLink(notification: Notification): string | null {
  if (!notification.link) return null;
  const metadata = notification.metadata as { requestId?: string } | null;
  if (metadata?.requestId && notification.link === '/absences/request') {
    return `${notification.link}?requestId=${metadata.requestId}`;
  }
  return notification.link;
}

function NotificationItem({
  notification,
  onMarkRead,
  onNavigate,
}: {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onNavigate: (link: string) => void;
}) {
  const link = buildNotificationLink(notification);
  // Clicking a notification marks it read (and follows its link if it has one).
  const handleClick = () => {
    if (!notification.isRead) onMarkRead(notification.id);
    if (link) onNavigate(link);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      className={cn(
        'flex gap-3 px-4 py-3 border-b border-border/50 last:border-0 transition-colors cursor-pointer hover:bg-muted/50',
        !notification.isRead && 'bg-primary/5',
      )}
    >
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm leading-tight',
            !notification.isRead ? 'font-semibold text-foreground' : 'text-muted-foreground',
          )}
        >
          {notification.title}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
          {notification.body}
        </p>
        <p className="text-[11px] text-muted-foreground/60 mt-1">
          {timeAgo(notification.createdAt)}
        </p>
      </div>
      {!notification.isRead && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMarkRead(notification.id);
          }}
          className="flex-shrink-0 self-start mt-1 p-1 rounded hover:bg-muted transition-colors shrink-0"
          aria-label="Mark as read"
        >
          <Check className="h-3 w-3 text-muted-foreground" />
        </button>
      )}
    </div>
  );
}

const INITIAL_LIMIT = 5;
const LOAD_MORE_LIMIT = 50;

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(INITIAL_LIMIT);
  const queryClient = useQueryClient();
  const { unreadCount, setUnreadCount, connect, disconnect } = useNotificationStore();
  const { data: serverCount } = useUnreadCount();
  const { data: notificationsData } = useNotifications(1, limit);
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const notifications = notificationsData?.items ?? [];
  const total = notificationsData?.total ?? 0;
  const hasMore = total > limit;
  const showLoadMore = hasMore && notifications.length > 0;

  const handleNotificationClick = (link: string) => {
    setOpen(false);
    navigate({ to: link });
  };

  useEffect(() => {
    if (serverCount !== undefined) {
      setUnreadCount(serverCount);
    }
  }, [serverCount, setUnreadCount]);

  useEffect(() => {
    if (!open) setLimit(INITIAL_LIMIT);
  }, [open]);

  useEffect(() => {
    connect(env.VITE_API_URL);
    return () => disconnect();
  }, [connect, disconnect]);

  useEffect(() => {
    const store = useNotificationStore;
    const unsub = store.subscribe((state, prevState) => {
      if (state.unreadCount > prevState.unreadCount) {
        queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      }
    });
    return unsub;
  }, [queryClient]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              className={cn(
                'relative flex h-9 w-9 items-center justify-center',
                'rounded-full bg-hygge/10',
                'transition-colors',
                'hover:bg-hygge/20',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'cursor-pointer',
              )}
              aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
            >
              <Bell className="h-4 w-4 text-hygge" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>Notifications</p>
        </TooltipContent>
      </Tooltip>

      <PopoverContent
        align="end"
        className="w-80 p-0 z-[100]"
        sideOffset={8}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllAsRead.mutate()}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
            >
              <CheckCheck className="h-3 w-3" />
              Mark all read
            </button>
          )}
        </div>

        <div className={cn('overflow-y-auto overflow-x-hidden', limit > INITIAL_LIMIT ? 'max-h-[400px]' : 'max-h-[320px]')}>
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Bell className="h-8 w-8 text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">No notifications yet</p>
            </div>
          ) : (
            notifications.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onMarkRead={(id) => markAsRead.mutate(id)}
                onNavigate={handleNotificationClick}
              />
            ))
          )}
        </div>
        {showLoadMore && (
          <button
            type="button"
            onClick={() => setLimit(Math.min(LOAD_MORE_LIMIT, total))}
            className="block w-full px-4 py-2 text-xs text-primary hover:bg-muted/50 border-t border-border text-center cursor-pointer"
          >
            View all ({total})
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
